import { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { MotiView } from "moti";
import TabNavigator from "../components/common/general/TabNavigator/TabNavigator";
import NoChatBarLayout from "../layouts/NoChatBarLayout.web";
import { useAppConfig } from "../AppConfigProvider";
import { RolePermissions } from "../constants/rolesAndPermissions";
import { useGetCourseByIdQuery, useGetUserPermissionsQuery } from "../graphql/generated/graphql";
import CourseInfoContainer from "../containers/courseSettings/CourseInfoContainer/CourseInfoContainer";
import DueDateControlsContainer from "../containers/courseSettings/DueDateControlsContainer/DueDateControlsContainer";
import CourseCatalogInfoContainer from "../containers/courseSettings/CourseCatalogInfoContainer/CourseCatalogInfoContainer";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import RosterManagementContainer from "../containers/courseSettings/RosterManagementContainer/RosterManagementContainer";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";

function CourseSettingsView() {
  const posthog = usePostHog();
  const { isMedium } = useResponsiveScreen();
  const { isAxioOffering, primaryColor } = useAppConfig();

  const route = useRoute<RouteProp<RootStackParamList, "Course Settings">>();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const [activeTab, setActiveTab] = useState(route.params?.tab ?? "courseInfo");

  useEffect(() => {
    setActiveTab(route.params?.tab ?? "courseInfo");
  }, [route.params?.tab]);

  const courseId = route.params?.courseId;

  const { data } = useGetUserPermissionsQuery();

  const rolePermissions = data?.getUserPermissions ?? [];

  const tabs = [
    { key: "courseInfo", title: "Course Info" },
    { key: "rosterManagement", title: "Roster Management" },
    { key: "dueDateControls", title: "Due Date Controls" },
    { key: "courseCatalogInfo", title: "Course Catalog Info" },
  ];

  useEffect(() => {
    posthog?.capture("Course Settings Page Visited");
  }, []);

  if (!rolePermissions.includes(RolePermissions.courseEdit)) {
    return (
      <NoChatBarLayout>
        <CustomText text="You do not have permission to access this page" />
      </NoChatBarLayout>
    );
  }

  const handleBackToCourseEditor = () => {
    navigation.navigate("Custom Coursework Modules", { courseId });
  };

  return (
    <NoChatBarLayout>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={10}
        transition={{
          all: { type: "timing", duration: 500 },
        }}>
        <View style={styles.container}>
          <TouchableOpacity onPress={handleBackToCourseEditor}>
            <CustomText
              useTranslationText={false}
              size="s"
              text="< Back to Course Editor"
              style={styles.breadcrumbText}
            />
          </TouchableOpacity>
          <View style={styles.tabContainer}>
            <CustomText
              useTranslationText={false}
              text={"Course Settings"}
              size={isMedium ? "l" : "xs"}
              textType="display"
              weight="ultraBold"
              role="heading"
              aria-level="1"
              style={undefined}
            />
          </View>
        </View>
      </MotiView>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={500}
        transition={{
          all: { type: "timing", duration: 600 },
        }}>
        <CustomSpacing type="vertical" size="s" />
        {isAxioOffering ? (
          <TabNavigator
            tabData={tabs}
            activeTab={activeTab}
            onTabPress={(key) => {
              navigation.navigate("Course Settings", { courseId, tab: key });
            }}
            activeTabColor={primaryColor}
            rightButtonComponent={undefined}
          />
        ) : null}
        <CustomSpacing type="vertical" size="l" />
      </MotiView>
      {activeTab === "courseInfo" ? <CourseInfoContainer courseId={courseId} /> : null}
      {activeTab === "rosterManagement" ? <RosterManagementContainer courseId={courseId} /> : null}
      {activeTab === "dueDateControls" ? <DueDateControlsContainer courseId={courseId} /> : null}
      {activeTab === "courseCatalogInfo" ? <CourseCatalogInfoContainer courseId={courseId} /> : null}
    </NoChatBarLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  tabContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  breadcrumbText: {
    color: "#6B7280",
    fontWeight: "500",
  },
});

export default CourseSettingsView;
